import React, { useRef, useState } from 'react';
import { ArrowUpRightIcon, MaximizeIcon, MousePointerClickIcon, PrinterIcon, TrophyIcon } from 'lucide-react';
import { ImageLightbox } from '../components/ImageLightbox';
import { BuilderLogoGrid } from '../components/BuilderLogoGrid';
import { InteractiveTourMap } from '../components/tour/InteractiveTourMap';
import { TourEntryList } from '../components/tour/TourEntryList';
import { TourEntryDetails } from '../components/tour/TourEntryDetails';
import { mapLegends, tourMap } from '../data/magazine';
import { tourEntries } from '../data/tourEntries';
import { featuredBuilders } from '../data/builders';
import { eventHours, eventYear, links } from '../data/event';
const mapImages = [tourMap, ...mapLegends];
export function ModelHomes() {
  const [index, setIndex] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const mobileCardRef = useRef<HTMLDivElement>(null);
  const displayed = hovered ?? selected;
  const displayedEntry = tourEntries.find(e => e.number === displayed) ?? null;
  const selectFromList = (n: number) => {
    setSelected(n);
    if (window.matchMedia('(max-width: 767px)').matches) {
      mobileCardRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };
  return <div data-id="element-338" data-mp="1rh1thrre3rea">
      <section className="border-b border-line bg-canvas" data-id="element-339" data-mp="1nh17nm1yodx7">
        <div className="mx-auto max-w-7xl px-5 py-12 lg:px-8 lg:py-16" data-id="element-340" data-mp="1nim1f9ggbsu0">
          <h1 className="font-display text-5xl font-bold uppercase text-navy" data-id="element-341" data-mp="1ih6e1aqsxa8g">Model Homes</h1>
          <p className="mt-3 max-w-2xl text-lg text-muted" data-id="element-342" data-mp="1dk69j49lofjg">
            Use the official tour map to find every Showcase entry across Cape Coral, then vote for your favorite.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-id="element-343" data-mp="37xe9tbgchjch">
            <a href={links.vote} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-md bg-orange-deep px-6 py-3.5 text-base font-semibold text-white transition-colors duration-150 hover:bg-orange-deeper" data-id="element-344" data-mp="37urzj422kpqk">
              <TrophyIcon className="h-5 w-5" aria-hidden="true" data-id="element-345" data-mp="rb7x1a9fy2rh" />
              Pick your favorite model
            </a>
            <a href={links.printableMap} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 rounded-md border border-line bg-white px-6 py-3.5 text-base font-semibold text-navy transition-colors duration-150 hover:border-navy" data-id="element-346" data-mp="waua9jp9rj0a">
              <PrinterIcon className="h-5 w-5" aria-hidden="true" data-id="element-347" data-mp="1b0rrffos78fz" />
              Printable map
              <span className="text-sm font-normal text-muted" data-id="element-348" data-mp="1eyh62x9wj1bc">(coming soon)</span>
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-8 px-5 py-14 lg:grid-cols-12 lg:px-8" data-id="element-349" data-mp="3lmznmcjj2yo">
        <div className="lg:col-span-8" data-id="element-350" data-mp="3s6qheu4w14lx">
          <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end" data-id="element-351" data-mp="3s5p1occxftxv">
            <div data-id="element-352" data-mp="3n6vrotwuml2g">
              <h2 className="font-display text-3xl font-bold uppercase text-ink" data-id="element-353" data-mp="3n7dwwg1ho9mi">Interactive tour map</h2>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-muted" data-id="element-354" data-mp="5z0l9aq2rbpd">
                <MousePointerClickIcon className="h-4 w-4" aria-hidden="true" data-id="element-355" data-mp="120esypbc6xq" />
                Hover or tap any number for the home, builder, and directions.
              </p>
            </div>
            <button type="button" onClick={() => setIndex(0)} className="inline-flex items-center gap-2 self-start whitespace-nowrap rounded-md border border-line bg-white px-3.5 py-2 text-sm font-semibold text-navy transition-colors duration-150 hover:border-navy sm:self-auto" data-id="element-356" data-mp="34iqpvrzr5a6a">
              <MaximizeIcon className="h-4 w-4" aria-hidden="true" data-id="element-357" data-mp="2qnwskkjzw3yv" />
              Printed map & legend
            </button>
          </div>

          <div className="mt-6 rounded-xl border border-line bg-white" data-id="element-358" data-mp="1p02s240ydmut">
            <InteractiveTourMap map={tourMap} entries={tourEntries} activeNumber={displayed} selectedNumber={selected} onHover={setHovered} onSelect={setSelected} data-id="element-359" data-mp="1t14ka0wgcyna" />
          </div>

          <div ref={mobileCardRef} className="md:hidden" data-id="element-360" data-mp="22t0uubfq6h8e">
            {displayedEntry ? <div className="mt-4 rounded-xl border border-line bg-white p-4 shadow-sm" data-id="element-361" data-mp="27prbazvfzeuc">
                <TourEntryDetails entry={displayedEntry} data-id="element-362" data-mp="2cnkf5m99vwrg" />
              </div> : <p className="mt-4 rounded-xl bg-canvas px-4 py-3 text-sm text-muted" data-id="element-363" data-mp="2gr8lk1m64mb4">Tap a number on the map to see details.</p>}
          </div>
        </div>

        <aside className="lg:col-span-4" data-id="element-364" data-mp="mdhijkych0on">
          <div className="flex h-[28rem] flex-col lg:sticky lg:top-28 lg:h-[calc(100vh-9rem)]" data-id="element-365" data-mp="meivxw2u1z9j">
            <div className="mb-3 flex items-baseline justify-between" data-id="element-366" data-mp="rc13pj5468fy">
              <h2 className="font-display text-2xl font-bold uppercase text-ink" data-id="element-367" data-mp="jtbab12p6o2a">All {tourEntries.length} entries</h2>
              <span className="text-xs text-muted" data-id="element-368" data-mp="ezymuu9r8nb4">Fri–Sun · {eventHours}</span>
            </div>
            <div className="min-h-0 flex-1" data-id="element-369" data-mp="a2zv2idkcsg3">
              <TourEntryList entries={tourEntries} activeNumber={displayed} onHover={setHovered} onSelect={selectFromList} data-id="element-370" data-mp="53nak4a5zxgo" />
            </div>
          </div>
        </aside>
      </section>

      <section className="border-t border-line bg-canvas" data-id="element-371" data-mp="xpyardtuzwcy">
        <div className="mx-auto max-w-7xl px-5 py-14 lg:px-8" data-id="element-372" data-mp="srws2u0chz20">
          <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-end" data-id="element-373" data-mp="orcvbrjooxge">
            <h2 className="font-display text-3xl font-bold uppercase text-ink" data-id="element-374" data-mp="otp8lrehyjn4">{eventYear} participating builders</h2>
            <p className="flex items-center gap-1 text-sm text-muted" data-id="element-375" data-mp="39httafwssoih">
              Tap a builder to visit their website
              <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-376" data-mp="3azsi74qt5qni" />
            </p>
          </div>
          <div className="mt-8" data-id="element-377" data-mp="3fwsq7l77x3h9">
            <BuilderLogoGrid builders={featuredBuilders} showNames data-id="element-378" data-mp="3fx2dh10vowj9" />
          </div>
        </div>
      </section>

      <ImageLightbox images={mapImages} index={index} onClose={() => setIndex(null)} onChange={setIndex} data-id="element-379" data-mp="312xjolujbq98" />
    </div>;
}